/**
 * AnswerOptionV2 - Individual answer option component
 *
 * Features:
 * - Animated selection state
 * - Cross-out button with visual feedback
 * - Keyboard accessibility
 * - Math/HTML content rendering
 */

import { memo, useCallback } from 'react'
import { renderContent as _renderContent, sanitizeAnswerHTML } from '../../../utils/contentRenderer'

// Content renderer helper - uses answer sanitizer by default
const renderContent = (content, contentType = 'md') =>
  _renderContent(content, contentType, sanitizeAnswerHTML)

// Cross-out icon (X to cross out, undo arrow to restore)
const CrossOutIcon = ({ isCrossedOut }) => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    {isCrossedOut ? (
      // Undo/restore icon
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4"
      />
    ) : (
      // X icon for cross-out
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    )}
  </svg>
)

const AnswerOptionV2 = memo(
  ({ option, label, isSelected, isCrossedOut, onSelect, onCrossOut, contentType }) => {
    // Handle click on option
    const handleClick = useCallback(() => {
      if (!isCrossedOut) {
        onSelect(label)
      }
    }, [isCrossedOut, onSelect, label])

    // Handle cross-out click
    const handleCrossOutClick = useCallback(
      (e) => {
        e.stopPropagation()
        onCrossOut(label)
      },
      [onCrossOut, label]
    )

    // Handle keyboard interaction
    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (!isCrossedOut) {
            onSelect(label)
          }
        }
      },
      [isCrossedOut, onSelect, label]
    )

    // NOTE: Using items-start instead of items-center is critical for MathML rendering.
    // items-center interferes with the browser's native MathML layout algorithm,
    // causing elements like <mroot> (nth roots) and <mfrac> (fractions) to render
    // incorrectly with misaligned components. See commit 2f36954 for details.
    return (
      <div
        className={`
        group relative flex items-start gap-3 sm:gap-4
        p-3 sm:p-4 rounded-xl border-2 cursor-pointer
        transition-all duration-200 ease-out
        touch-manipulation
        ${
          isSelected
            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-md'
            : isCrossedOut
              ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800/50 opacity-60'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 [@media(hover:hover)]:hover:border-indigo-300 [@media(hover:hover)]:dark:hover:border-indigo-600 [@media(hover:hover)]:hover:shadow-sm'
        }
      `}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        aria-disabled={isCrossedOut}
      >
        {/* Strikethrough line across entire card when crossed out */}
        {isCrossedOut && (
          <div className="absolute inset-0 flex items-center pointer-events-none z-10">
            <div className="w-full h-0.5 bg-red-400 dark:bg-red-500"></div>
          </div>
        )}

        {/* Option label circle */}
        <div
          className={`
          flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full
          flex items-center justify-center font-bold text-sm
          transition-all duration-200
          ${
            isSelected
              ? 'bg-indigo-500 text-white shadow-md scale-110'
              : isCrossedOut
                ? 'bg-red-200 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 [@media(hover:hover)]:group-hover:bg-indigo-100 [@media(hover:hover)]:dark:group-hover:bg-indigo-900/30 [@media(hover:hover)]:group-hover:text-indigo-600 [@media(hover:hover)]:dark:group-hover:text-indigo-400'
          }
        `}
        >
          {label}
        </div>

        {/* Option content */}
        <div className={`flex-1 min-w-0 ${isCrossedOut ? 'text-gray-400 dark:text-gray-500' : ''}`}>
          <div className="question-content text-sm sm:text-base text-gray-800 dark:text-gray-200">
            {renderContent(option.content, contentType)}
          </div>
        </div>

        {/* Cross-out button */}
        <button
          onClick={handleCrossOutClick}
          className={`
          flex-shrink-0 p-2 rounded-lg transition-all duration-200 relative z-20
          touch-manipulation
          ${
            isCrossedOut
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 [@media(hover:hover)]:hover:bg-red-200 [@media(hover:hover)]:dark:hover:bg-red-900/50'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 [@media(hover:hover)]:hover:bg-gray-200 [@media(hover:hover)]:dark:hover:bg-gray-600 [@media(hover:hover)]:hover:text-gray-600 [@media(hover:hover)]:dark:hover:text-gray-300'
          }
          opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus:opacity-100
        `}
          title={isCrossedOut ? 'Undo cross out' : 'Cross out this answer'}
          aria-label={isCrossedOut ? `Undo cross out for ${label}` : `Cross out answer ${label}`}
        >
          <CrossOutIcon isCrossedOut={isCrossedOut} />
        </button>

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute -right-1 -top-1 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center shadow-md">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
      </div>
    )
  }
)

AnswerOptionV2.displayName = 'AnswerOptionV2'

export default AnswerOptionV2
